import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { AlertTriangle, ArrowLeft, Download, Headphones, Loader2 } from 'lucide-react';
import { apiService } from '../services/api';

// Un texte lu à voix haute par la synthèse vocale locale (backend services/audio_render.py) : Nexus donne le lien de
// cette page ; elle suit la préparation sur le Mac, puis lit le MP3 et propose de le télécharger (écoute en voiture).
// Le fichier se lit avec le jeton de la personne : il est chargé en mémoire puis donné au lecteur.

const POLL_MS = 15000;

const minutes = (seconds) => {
  const total = Math.round(Number(seconds) || 0);
  return `${Math.floor(total / 60)} min ${String(total % 60).padStart(2, '0')} s`;
};

const AudioPage = () => {
  const { audioId } = useParams();
  const navigate = useNavigate();
  const [item, setItem] = useState(null);
  const [error, setError] = useState('');
  const [src, setSrc] = useState('');
  const urlRef = useRef('');

  useEffect(() => {
    let stopped = false;
    let timer = null;
    const load = async () => {
      try {
        const data = await apiService.get(`/audio/${audioId}`);
        if (stopped) return;
        setItem(data);
        setError('');
        if (data.status === 'queued' || data.status === 'working') timer = setTimeout(load, POLL_MS);
      } catch (e) {
        if (!stopped) setError(e?.message || 'Audio introuvable.');
      }
    };
    load();
    return () => { stopped = true; clearTimeout(timer); };
  }, [audioId]);

  useEffect(() => {
    if (item?.status !== 'done' || urlRef.current) return undefined;
    let cancelled = false;
    fetch(`${apiService.getBaseUrl()}/audio/${audioId}/file`, { headers: apiService.getHeaders() })
      .then((response) => (response.ok ? response.blob() : Promise.reject(new Error('Fichier indisponible.'))))
      .then((blob) => {
        if (cancelled) return;
        urlRef.current = URL.createObjectURL(blob);
        setSrc(urlRef.current);
      })
      .catch((e) => !cancelled && setError(e.message));
    return () => { cancelled = true; };
  }, [item?.status, audioId]);

  useEffect(() => () => urlRef.current && URL.revokeObjectURL(urlRef.current), []);

  const fileName = `${(item?.title || 'audio').replace(/[^\w\- À-ÿ]+/g, '').trim() || 'audio'}.mp3`;
  const waiting = item && (item.status === 'queued' || item.status === 'working');

  return (
    <div className="mx-auto max-w-2xl space-y-5 px-4 py-6 sm:px-0">
      <button type="button" onClick={() => navigate(-1)} className="flex items-center gap-1 text-sm font-medium text-slate-500 hover:text-slate-800">
        <ArrowLeft size={15} /> Retour
      </button>

      <section className="rounded-2xl border border-slate-200/80 bg-white p-5 shadow-sm sm:p-6">
        <div className="flex items-start gap-3">
          <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-indigo-50 text-indigo-600">
            <Headphones size={22} />
          </span>
          <div className="min-w-0">
            <h1 className="text-xl font-semibold tracking-tight text-slate-950">{item?.title || 'Audio'}</h1>
            <p className="mt-0.5 text-sm text-slate-500">
              Lu par la synthèse vocale d'Innowide (sur nos serveurs){item?.duration_seconds ? ` · ${minutes(item.duration_seconds)}` : ''}
            </p>
          </div>
        </div>

        {error && (
          <p className="mt-4 flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
            <AlertTriangle size={16} className="mt-0.5 shrink-0" /> {error}
          </p>
        )}

        {!item && !error && <p className="mt-4 text-sm text-slate-500">Chargement…</p>}

        {waiting && (
          <p className="mt-4 flex items-center gap-2 text-sm text-slate-600">
            <Loader2 size={16} className="animate-spin text-indigo-500" />
            {item.status === 'queued' ? 'En file d’attente' : 'Préparation de la voix'}
            {item.ready_in_minutes ? ` · environ ${item.ready_in_minutes} min` : ''}. Tu recevras une notification quand il sera prêt.
          </p>
        )}

        {item?.status === 'failed' && (
          <p className="mt-4 text-sm text-rose-700">La synthèse n'a pas abouti{item.error ? ` : ${item.error}` : ''}. Demande à Nexus de relancer.</p>
        )}

        {item?.status === 'done' && (
          <div className="mt-5 space-y-3">
            <audio controls src={src || undefined} className="w-full" preload="metadata" />
            <a
              href={src || undefined}
              download={fileName}
              className={`inline-flex items-center gap-2 rounded-xl px-4 py-2 text-sm font-semibold ${src ? 'bg-slate-900 text-white hover:bg-slate-800' : 'pointer-events-none bg-slate-100 text-slate-400'}`}
            >
              <Download size={16} /> Télécharger le MP3
            </a>
          </div>
        )}
      </section>
    </div>
  );
};

export default AudioPage;
